import Alpine from 'alpinejs'
import { dispatch } from '../../node_modules/alpinejs/src/utils/dispatch'

/**
 * dispatches:
 * - cart-item-added {
 *       productId: string,
 *       variantId: string,
 *       item: {},
 *   }
 * - cart-item-add-error {
 *       message: string,
 *       productId: string,
 *       variantId: string
 *   }
 */
Alpine.store('cart', {
    
    state: {
        status: 'valid',
        numberOfItems: 0,
        numberOfItemsText: '',
        voucher: {
            status: 'none',
            code: null,
            description: null
        },
        items: [],
        amounts: {
            items: {
                subtotal: {
                    text: null,
                    amountMCU: 0
                },
                discount: {
                    text: null,
                    amountMCU: 0
                },
                total: {
                    text: null,
                    amountMCU: 0
                }
            },
            shipping: {
                subtotal: {
                    text: null,
                    amountMCU: 0
                },
                discount: {
                    text: null,
                    amountMCU: 0
                },
                total: {
                    text: null,
                    amountMCU: 0
                }
            },
            total: {
                text: null,
                amountMCU: 0
            }
        },
        voucherForm: {
            invalids: [],
            feedback: [],
            properties: {
                code: {
                    value: '',
                    invalids: [],
                    feedback: []
                }
            }
        },
        alerts: []
    },
    fetchUrl: null,
    voucherRedeemUrl: null,
    voucherRemoveUrl: null,
    isLegacy: true, // legacy cart behavior
    
    init() {
        let awaitPaymentTimeout = null
        Alpine.watch(
            () => this.state.status, 
            value => {
                if(value === 'awaiting_payment') {
                    awaitPaymentTimeout = window.setTimeout(() => this.update(), 3000)
                } else {
                    window.clearTimeout(awaitPaymentTimeout)   
                }
                if(value === 'continue_to_success') {
                    document.location = this.state.successUrl
                }
            }
        )
    },
    
    async setup(fetchUrl, numberOfItems, isLegacy, voucherRedeemUrl=null, voucherRemoveUrl=null) {
        this.fetchUrl = fetchUrl
        this.state.numberOfItems = numberOfItems
        this.isLegacy = isLegacy
        this.voucherRedeemUrl = voucherRedeemUrl
        this.voucherRemoveUrl = voucherRemoveUrl
    },

    async update() {
        return fetch(this.fetchUrl, {
            credentials: 'include',
        }).then(response => {
            if(!response.ok) {
                throw new Error('response status '+response.status+'/'+response.statusText)
            }
            return response.json()
        }).then(data => this.state=data)
    },
    
    async addProduct(addProductUrl, productId, variantId) {
        
        const options = <RequestInit>{
            credentials: 'include'
        }
        if(this.isLegacy) {
            await Alpine.store('session').updateState('cart-add-product')
            addProductUrl = addProductUrl.replace('{form_key}', Alpine.store('session').token)
        } else {
            options.method = 'POST'
        }
        
        return fetch(addProductUrl, options).then(response => {
            if(!response.ok) {
                throw new Error('response status '+response.status+'/'+response.statusText)
            }
            return response.json()
        }).then(data => {
            
            const returnItem = () => {
                const item = this.getAllItems().sort((a,b) => b.quoteItemId-a.quoteItemId).find(item => item.productId === productId && item.variantId == variantId)
                dispatch(window, 'cart-item-added', {
                    productId: productId,
                    variantId: variantId,
                    item: item,
                })
                return item
            };
            
            if(data.status === 'redirect') {
                document.location = data.location
                return null
            }
            if(data.status === 'success') {
                if(this.isLegacy) {
                    return this.update().then(() => {
                        return returnItem()
                    })
                } else {
                    delete data.status
                    this.state = data
                    return returnItem()
                }
            }
            
            if(data.error) {
                dispatch(window, 'cart-item-add-error', {
                    message: data.error,
                    productId: productId,
                    variantId: variantId,
                })
            }
        })
    },

    async removeItem(item) {

        let removeUrl = null
        const options = <RequestInit>{
            credentials: 'include'
        }
        if(this.isLegacy) {
            await Alpine.store('session').updateState('cart-remove-item')
            removeUrl = item.removeUrlTemplate.replace('{form_key}', Alpine.store('session').token)
        } else {
            removeUrl = item.removeUrlTemplate
            options.method = 'POST'
        }
        
        return fetch(removeUrl, options)
            .then(response => {
                if(!response.ok) {
                    throw new Error('response status '+response.status+'/'+response.statusText)
                }
                return response.json()
            })
            .then(data => {
                if(data.status === 'redirect') {
                    document.location = data.location
                    return null
                }
                if(data.status === 'success') {
                    const itemRemoved = () => {
                        dispatch(window, 'cart-item-removed', {
                            productId: item.productId,
                            variantId: item.variantId,
                            item: item,
                        })
                    }
                    if(this.isLegacy) {
                        this.update().then(() => {
                            itemRemoved()
                        })                    
                    } else {
                        delete data.status
                        this.state = data
                        itemRemoved()
                    }
                }
            })
    },
    
    async redeemVoucher() {
        return fetch(this.voucherRedeemUrl, {
            credentials: 'include',
            method: 'POST',
            headers: { 'Content-Type' : 'application/json' },
            body: JSON.stringify({
                code: this.state.voucherForm.properties.code.value
            })
        }).then(response => {
            if(!response.ok) {
                throw new Error('response status '+response.status+'/'+response.statusText)
            }
            return response.json()
        }).then(data => {
            this.state = data
        })
    },
    
    async removeVoucher() {
        return fetch(this.voucherRemoveUrl, {
            credentials: 'include',
            method: 'POST',
        }).then(response => {
            if(!response.ok) {
                throw new Error('response status '+response.status+'/'+response.statusText)
            }
            return response.json()
        }).then(data => {
            this.state = data
        })
    },
    
    getItem(variantId) {
        return this.getAllItems().find(item => item.variantId === variantId)
    },

    getItemImageUrl(item, minSize) {
        return item.productData.images.find(image => image.width >= minSize)?.url || '#'
    },

    getAllItems() {
        const items = []
        for(const item of this.state.items) {
            const queue = [ item ]
            while(queue.length > 0) {
                const node = queue.shift()
                if(!node.isAvailable) {
                    continue
                }
                items.push(node)
                queue.push(...node.subItems)
            }
        }
        return items
    },
    
    isContinueToPayment() {
        return this.state.status === 'continue_to_payment'
    },
    
    isAwaitingPayment() {
        return this.state.status === 'awaiting_payment'
    },
    
    isEmpty() {
        return this.state.status === 'empty'
    },
    
    canEdit() {
        return !this.isContinueToPayment() && !this.isAwaitingPayment()
    },
    
})